/* ==============================================================================
   Painel flutuante — menus de contexto e popovers
   ==============================================================================
   O que este componente resolve, e por que não era só estética
   ===========================================================
   Havia três menus flutuantes na interface — o do usuário na barra superior, o
   de contexto da lista de membros, e o de ações do palco — cada um com o seu
   posicionamento, o seu "fecha ao clicar fora", o seu Escape e o seu raio de
   canto. Três implementações do mesmo comportamento significam três lugares para
   o mesmo defeito, e foi assim que um deles ficou sem correção de borda: aberto
   perto do rodapé, saía da tela.

   Duas coisas que quase toda implementação de popover erra
   =======================================================

   1. **Corrigir a posição DEPOIS de medir, não antes.** Só depois de montar se
      sabe a altura real do painel — o conteúdo é variável. Por isso a posição é
      ajustada num `useLayoutEffect`: medir, comparar com a viewport, e virar
      para o outro lado se não couber. Em `useEffect` o salto seria visível.

   2. **`pointerdown` para fechar, e não `click`.** Com `click`, apertar o botão
      que ABRE o menu enquanto ele está aberto produz um fecha-e-abre no mesmo
      gesto, e o menu parece não responder. `pointerdown` fecha antes de o clique
      completar.

   Escape é registrado em CAPTURA para o popover mais interno ganhar de um
   diálogo que o contenha.
   ============================================================================== */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** Folga mínima entre o painel e a borda da janela. */
const VIEWPORT_MARGIN = 10;

export interface FloatingPanelProps {
  /** Âncora em coordenadas de VIEWPORT — normalmente `event.clientX/Y`. */
  x: number;
  y: number;
  onClose: () => void;
  children: ReactNode;
  /** Largura fixa. Popover é painel estreito e denso; largo vira diálogo. */
  width?: number;
  /** Rótulo acessível. */
  label: string;
  /** `menu` para lista de ações, `dialog` para painel com controles. */
  role?: 'menu' | 'dialog';
}

export function FloatingPanel({
  x,
  y,
  onClose,
  children,
  width = 240,
  label,
  role = 'menu',
}: FloatingPanelProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x, y });

  /*
   * Correção de borda, medida DEPOIS da montagem.
   *
   * Vira para cima quando não cabe embaixo, e para a esquerda quando não cabe à
   * direita — em vez de simplesmente grudar na borda. Grudar deixaria o painel
   * cobrindo o cursor, e o cursor está exatamente sobre o item que a pessoa
   * acabou de escolher.
   */
  /*
   * A posição é recalculada quando o CONTEÚDO muda de tamanho, não só quando a
   * âncora muda — e essa era a origem de um defeito visível.
   * ==========================================================================
   * A versão anterior tinha `[x, y]` nas dependências e mais nada. Para um menu
   * de conteúdo estático isso basta: monta, mede, vira se não couber.
   *
   * Mas o seletor de dispositivos preenche a lista de forma ASSÍNCRONA
   * (`enumerateDevices` é uma promise). Na primeira medição o painel estava
   * praticamente vazio — caberia em qualquer lugar, então nenhuma virada era
   * necessária. Os dispositivos chegavam, o painel crescia PARA BAIXO a partir
   * da posição já fixada, e a parte de baixo saía da tela. Como a âncora fica
   * logo acima da barra inferior, "para baixo" significava fora da viewport: o
   * relato foi "não consigo ver as opções do fone, o menu fica muito pra baixo
   * da toolbar".
   *
   * `ResizeObserver` fecha isso para qualquer popover com conteúdo assíncrono,
   * e não só para este caso.
   */
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const reposition = () => {
      const rect = element.getBoundingClientRect();

      let nextX = x;
      let nextY = y;

      if (x + rect.width > window.innerWidth - VIEWPORT_MARGIN) {
        nextX = Math.max(VIEWPORT_MARGIN, x - rect.width);
      }
      if (y + rect.height > window.innerHeight - VIEWPORT_MARGIN) {
        /*
         * Vira para cima. O `max` evita sair pelo topo quando o painel é mais
         * alto que a viewport — nesse caso ele encosta no topo e rola por
         * dentro, que é melhor que ficar com a metade de cima invisível.
         */
        nextY = Math.max(VIEWPORT_MARGIN, y - rect.height);
      }

      /*
       * Só chama `setPosition` se algo MUDOU.
       *
       * O `ResizeObserver` dispara na montagem também, e um `setPosition`
       * incondicional a cada disparo re-renderiza, o que pode mudar o layout e
       * disparar o observer de novo — o laço clássico de observador que reage à
       * própria reação.
       */
      setPosition((current) =>
        current.x === nextX && current.y === nextY ? current : { x: nextX, y: nextY },
      );
    };

    reposition();

    /*
     * `ResizeObserver` pode não existir em ambiente de teste (jsdom antigo,
     * Node sem DOM). A ausência degrada para o comportamento anterior — uma
     * medição só — em vez de lançar.
     */
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(reposition);
    observer.observe(element);
    return () => observer.disconnect();
  }, [x, y]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    /*
     * O `setTimeout` de 0 não é gambiarra: o `pointerdown` que ABRIU este painel
     * pode ainda estar propagando quando o efeito registra o ouvinte, e o painel
     * fecharia no mesmo gesto que o abriu. Adiar um tick tira o registro do
     * caminho do evento em curso.
     */
    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', onPointerDown);
    }, 0);
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role={role}
      {...(role === 'dialog' ? { 'aria-modal': false } : {})}
      aria-label={label}
      className="glass fixed py-1.5"
      style={{
        left: position.x,
        top: position.y,
        width,
        borderRadius: 'var(--radius-lg)',
        /* Sombra longa e um brilho fraco. O brilho separa o painel do conteúdo
           escuro por baixo sem precisar de contorno — que é o traço que a
           direção visual deste projeto evita. */
        boxShadow:
          'var(--glass-edge), 0 18px 48px oklch(0% 0 0 / 0.55), var(--glow-soft)',
        zIndex: 'var(--z-dropdown)',
        animation: 'popover-in var(--duration-fast) var(--ease-out-expo) both',
      }}
    >
      <style>{`
        @keyframes popover-in {
          from { opacity: 0; transform: translateY(-4px) scale(0.98) }
          to   { opacity: 1; transform: none }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes popover-in { from { opacity: 0 } to { opacity: 1 } }
        }
      `}</style>
      {children}
    </div>
  );
}

/* ==============================================================================
   Peças do painel
============================================================================== */

/** Micro-rótulo de grupo dentro do popover. */
export function PopoverLabel({ children }: { children: ReactNode }) {
  return (
    <p
      className="px-3 pb-1 pt-1.5 font-semibold uppercase"
      style={{
        fontSize: 9,
        letterSpacing: 'var(--tracking-wider)',
        color: 'var(--color-text-tertiary)',
      }}
    >
      {children}
    </p>
  );
}

export function PopoverDivider() {
  return (
    <span
      aria-hidden="true"
      className="my-1 block"
      style={{ height: 1, background: 'var(--color-border-subtle)' }}
    />
  );
}

/**
 * Linha do popover: ícone + título + subtítulo + chevron.
 *
 * O subtítulo é o que diferencia isto de um item de menu comum, e o motivo é o
 * mesmo da navegação de configurações: "Dispositivos" não diz nada, "Microfone,
 * fone e câmera" diz. Num popover de 240px cabe, e evita abrir para descobrir.
 */
export function PopoverRow({
  icon,
  title,
  subtitle,
  trailing,
  onClick,
  danger = false,
  chevron = false,
  disabled = false,
  selected = false,
}: {
  icon?: ReactNode;
  title: string;
  subtitle?: string;
  /** Valor atual à direita, quando a linha abre outra coisa. */
  trailing?: ReactNode;
  onClick?: (() => void) | undefined;
  danger?: boolean;
  chevron?: boolean;
  disabled?: boolean;
  selected?: boolean;
}) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={disabled}
      aria-disabled={disabled}
      className="flex w-full items-center px-3 py-1.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45"
      style={{
        gap: 'var(--spacing-2)',
        background: selected
          ? 'var(--color-accent-subtle)'
          : hover && !disabled
            ? danger
              ? 'var(--color-danger-subtle)'
              : 'oklch(100% 0 0 / 0.07)'
            : 'transparent',
        transitionDuration: 'var(--duration-fast)',
      }}
    >
      {icon !== undefined && (
        <span aria-hidden="true" className="grid shrink-0 place-items-center"
              style={{
                width: 18,
                color: danger
                  ? 'var(--color-danger-on-subtle)'
                  : selected
                    ? 'var(--color-accent-on-subtle)'
                    : 'var(--color-text-tertiary)',
              }}>
          {icon}
        </span>
      )}

      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium leading-tight"
              style={{
                fontSize: 'var(--text-xs)',
                color: danger
                  ? 'var(--color-danger-on-subtle)'
                  : selected
                    ? 'var(--color-accent-on-subtle)'
                    : 'var(--color-text-secondary)',
              }}>
          {title}
        </span>
        {subtitle !== undefined && (
          <span className="block truncate leading-tight"
                style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}>
            {subtitle}
          </span>
        )}
      </span>

      {trailing !== undefined && (
        <span className="shrink-0"
              style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}>
          {trailing}
        </span>
      )}

      {chevron && (
        <span aria-hidden="true" className="shrink-0"
              style={{ color: 'var(--color-text-disabled)' }}>
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
            <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7"
                  strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      )}
    </button>
  );
}

/** Cabeçalho do popover: quem/o que este painel controla. */
export function PopoverHeader({
  title,
  subtitle,
  leading,
}: {
  title: string;
  subtitle?: string;
  leading?: ReactNode;
}) {
  return (
    <div className="flex items-center px-3 pb-2 pt-1" style={{ gap: 'var(--spacing-2)' }}>
      {leading}
      <div className="min-w-0">
        <p className="truncate font-semibold"
           style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-primary)' }}>
          {title}
        </p>
        {subtitle !== undefined && (
          <p className="truncate"
             style={{ fontSize: 'var(--text-2xs)', color: 'var(--color-text-tertiary)' }}>
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}
